import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Agriculture,
  Groups,
  Refresh,
  Sensors,
  Spa,
  TrendingUp,
  VerifiedUser,
} from "@mui/icons-material";
import { LayoutDashboard, Map as MapIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "../api/api";
import AdminSensorsMap from "./AdminSensorsMap";
import "./AdminOverview.css";

const PERIOD_OPTIONS = [
  { value: "7d", labelKey: "admin_overview_period_7d" },
  { value: "30d", labelKey: "admin_overview_period_30d" },
  { value: "12w", labelKey: "admin_overview_period_12w" },
  { value: "12m", labelKey: "admin_overview_period_12m" },
];

const ROLE_COLORS = {
  ROLE_USER: "#26a6b7",
  ROLE_SUPPLIER: "#33b8d6",
  ROLE_ADMIN: "#f2b84b",
};

const roleLabelKeys = {
  ROLE_USER: "admin_overview_role_users",
  ROLE_SUPPLIER: "admin_overview_role_suppliers",
  ROLE_ADMIN: "admin_overview_role_admins",
};

const summaryCards = [
  {
    key: "totalUsers",
    titleKey: "admin_overview_total_users",
    icon: Groups,
    tone: "#e8f7f9",
    accent: "#26a6b7",
  },
  {
    key: "activeUsers",
    titleKey: "admin_overview_active_accounts",
    icon: VerifiedUser,
    tone: "#e8f7f9",
    accent: "#26a6b7",
  },
  {
    key: "totalSensors",
    titleKey: "admin_overview_total_sensors",
    icon: Sensors,
    tone: "#e8f7fb",
    accent: "#20aeca",
  },
  {
    key: "activeSensors",
    titleKey: "active_sensors",
    statusKey: "admin_overview_live_status",
    icon: Sensors,
    tone: "#eaf8f1",
    accent: "#1f9c70",
  },
  {
    key: "geolocatedSensors",
    titleKey: "admin_overview_geolocated_sensors",
    icon: TrendingUp,
    tone: "#edf6ff",
    accent: "#3284d6",
  },
  {
    key: "totalFields",
    titleKey: "admin_overview_total_fields",
    icon: Spa,
    tone: "#fff7e5",
    accent: "#e4a72d",
  },
  {
    key: "totalFarms",
    titleKey: "admin_overview_total_farms",
    icon: Agriculture,
    tone: "#f1effb",
    accent: "#7868c7",
  },
];

const defaultOverview = {
  summary: {},
  summaryChanges: {},
  trends: { users: [], sensors: [] },
  userRoles: [],
  recent: { users: [], sensorInstallations: [] },
};

const formatNumber = (value, language) =>
  new Intl.NumberFormat(language || "fr").format(Number(value || 0));

const formatDate = (value, language) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat(language || "fr", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatDateTime = (value, language) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat(language || "fr", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
};

const hasActivity = (data) =>
  Array.isArray(data) && data.some((item) => Number(item.count || 0) > 0);

const CustomTooltip = ({ active, payload, label, language }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="admin-overview-tooltip">
      <span>{label}</span>
      <strong>{formatNumber(payload[0].value, language)}</strong>
    </div>
  );
};

const StatSkeleton = () => (
  <div className="admin-overview-card admin-overview-stat">
    <div className="admin-overview-stat-top">
      <div className="admin-overview-skeleton icon" />
      <div className="admin-overview-skeleton line" style={{ width: "58%" }} />
    </div>
    <div className="admin-overview-skeleton value" />
    <div className="admin-overview-skeleton line" style={{ width: "68%" }} />
  </div>
);

const StatCard = ({ card, value, change, loading, t, language }) => {
  if (loading) return <StatSkeleton />;

  const Icon = card.icon;
  const numericChange = Number(change || 0);

  return (
    <article className="admin-overview-card admin-overview-stat">
      <div className="admin-overview-stat-top">
        <span
          className="admin-overview-stat-icon"
          style={{ backgroundColor: card.tone, color: card.accent }}
        >
          <Icon fontSize="small" />
        </span>
        <p className="admin-overview-stat-title">{t(card.titleKey)}</p>
      </div>
      <div className="admin-overview-stat-value">
        {formatNumber(value, language)}
      </div>
      <div
        className={
          "admin-overview-change " +
          (card.statusKey || numericChange > 0
            ? "is-positive"
            : "is-neutral")
        }
      >
        <span className="admin-overview-change-dot" aria-hidden="true" />
        {card.statusKey ? (
          t(card.statusKey)
        ) : (
          <>
            {numericChange > 0 ? "+" : ""}
            {formatNumber(numericChange, language)} {t("admin_overview_during_period")}
          </>
        )}
      </div>
    </article>
  );
};

const ChartSkeleton = () => <div className="admin-overview-skeleton chart" />;

const ChartEmptyState = ({ children }) => (
  <div className="admin-overview-empty admin-overview-chart-empty">
    <span className="admin-overview-empty-icon" aria-hidden="true">
      <TrendingUp fontSize="small" />
    </span>
    <span>{children}</span>
  </div>
);

const UsersTrendChart = ({ data, language, emptyLabel }) => {
  if (!hasActivity(data)) return <ChartEmptyState>{emptyLabel}</ChartEmptyState>;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 14, right: 10, bottom: 0, left: -22 }}>
        <defs>
          <linearGradient id="usersTrendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#26a6b7" stopOpacity={0.28} />
            <stop offset="100%" stopColor="#26a6b7" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#edf2f2" vertical={false} />
        <XAxis
          dataKey="label"
          axisLine={false}
          tickLine={false}
          minTickGap={28}
          tick={{ fill: "#7a8a96", fontSize: 11 }}
          tickMargin={10}
        />
        <YAxis
          allowDecimals={false}
          axisLine={false}
          tickLine={false}
          width={38}
          tick={{ fill: "#7a8a96", fontSize: 11 }}
        />
        <Tooltip content={<CustomTooltip language={language} />} />
        <Area
          type="monotone"
          dataKey="count"
          stroke="#26a6b7"
          strokeWidth={2.5}
          fill="url(#usersTrendFill)"
          dot={false}
          activeDot={{ r: 5, strokeWidth: 3, fill: "#ffffff" }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const SensorsTrendChart = ({ data, language, emptyLabel }) => {
  if (!hasActivity(data)) return <ChartEmptyState>{emptyLabel}</ChartEmptyState>;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 14, right: 10, bottom: 0, left: -22 }}>
        <CartesianGrid stroke="#edf2f2" vertical={false} />
        <XAxis
          dataKey="label"
          axisLine={false}
          tickLine={false}
          minTickGap={28}
          tick={{ fill: "#7a8a96", fontSize: 11 }}
          tickMargin={10}
        />
        <YAxis
          allowDecimals={false}
          axisLine={false}
          tickLine={false}
          width={38}
          tick={{ fill: "#7a8a96", fontSize: 11 }}
        />
        <Tooltip content={<CustomTooltip language={language} />} />
        <Bar
          dataKey="count"
          radius={[6, 6, 2, 2]}
          fill="#33b8d6"
          maxBarSize={28}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

const RoleDistribution = ({ roles, t, language }) => {
  const total = roles.reduce((sum, role) => sum + Number(role.count || 0), 0);
  const data = roles.map((role) => ({
    ...role,
    count: Number(role.count || 0),
    name: t(roleLabelKeys[role.role] || role.role),
    fill: ROLE_COLORS[role.role] || "#b8c4cc",
  }));

  if (total === 0) {
    return <ChartEmptyState>{t("admin_overview_no_data")}</ChartEmptyState>;
  }

  return (
    <div className="admin-overview-role-layout">
      <div className="admin-overview-role-chart-wrap">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="count"
              nameKey="name"
              innerRadius="66%"
              outerRadius="88%"
              paddingAngle={3}
              stroke="none"
            >
              {data.map((entry) => (
                <Cell key={entry.role} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => formatNumber(value, language)} />
          </PieChart>
        </ResponsiveContainer>
        <div className="admin-overview-role-center" aria-hidden="true">
          <strong>{formatNumber(total, language)}</strong>
          <span>{t("admin_overview_accounts")}</span>
        </div>
      </div>
      <div className="admin-overview-role-legend">
        {data.map((role) => (
          <div className="admin-overview-role-item" key={role.role}>
            <span
              className="admin-overview-role-dot"
              style={{ backgroundColor: role.fill }}
              aria-hidden="true"
            />
            <span className="admin-overview-role-name">{role.name}</span>
            <strong>{formatNumber(role.count, language)}</strong>
          </div>
        ))}
      </div>
    </div>
  );
};

const EmptyState = ({ children }) => (
  <div className="admin-overview-empty">{children}</div>
);

const RecentUsers = ({ users, t, language }) => {
  if (!users.length) {
    return <EmptyState>{t("admin_overview_no_recent_users")}</EmptyState>;
  }

  return (
    <div className="admin-overview-list">
      {users.map((user) => (
        <div className="admin-overview-list-item" key={user.uid || user.id}>
          <div className="admin-overview-list-identity">
            <span className="admin-overview-avatar" aria-hidden="true">
              {getInitials(user.name)}
            </span>
            <div className="admin-overview-list-copy">
              <div className="admin-overview-primary">{user.name || "-"}</div>
              <div className="admin-overview-secondary">{user.email || "-"}</div>
            </div>
          </div>
          <div className="admin-overview-list-meta">
            <div className="admin-overview-primary">
              {t(roleLabelKeys[user.role] || user.role)}
            </div>
            <div className="admin-overview-secondary">
              {formatDate(user.createdAt, language)}
            </div>
          </div>
          <span
            className={
              "admin-overview-badge " +
              (user.isActive ? "is-active" : "is-inactive")
            }
          >
            {user.isActive ? t("active") : t("inactive")}
          </span>
        </div>
      ))}
    </div>
  );
};

const RecentInstallations = ({ installations, t, language }) => {
  if (!installations.length) {
    return <EmptyState>{t("admin_overview_no_recent_installations")}</EmptyState>;
  }

  return (
    <div className="admin-overview-list">
      {installations.map((installation) => (
        <div className="admin-overview-list-item" key={installation.id}>
          <div className="admin-overview-list-identity">
            <span className="admin-overview-avatar is-sensor" aria-hidden="true">
              <Sensors fontSize="small" />
            </span>
            <div className="admin-overview-list-copy">
              <div className="admin-overview-primary">
                {installation.sensorName || installation.sensorCode || "-"}
              </div>
              <div className="admin-overview-secondary">
                {installation.sensorCode || "-"}
              </div>
            </div>
          </div>
          <div className="admin-overview-list-meta">
            <div className="admin-overview-primary">
              {installation.fieldName || "-"}
            </div>
            <div className="admin-overview-secondary">
              {formatDate(installation.installationDate, language)}
            </div>
          </div>
          <span
            className={
              "admin-overview-badge " +
              (installation.status === "installed"
                ? "is-installed"
                : "is-removed")
            }
          >
            {installation.status === "installed"
              ? t("admin_overview_installed")
              : t("admin_overview_removed")}
          </span>
        </div>
      ))}
    </div>
  );
};

const AdminOverview = () => {
  const { t, i18n } = useTranslation();
  const [period, setPeriod] = useState("30d");
  const [overview, setOverview] = useState(defaultOverview);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("users");
  const [viewMode, setViewMode] = useState("overview");
  const [sensorLocations, setSensorLocations] = useState([]);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState("");
  const [locationsGeneratedAt, setLocationsGeneratedAt] = useState(null);
  const mountedRef = useRef(false);
  const requestControllerRef = useRef(null);
  const locationsRequestControllerRef = useRef(null);

  const language = i18n.language || "fr";

  const fetchOverview = useCallback(
    async ({ silent = false } = {}) => {
      requestControllerRef.current?.abort();
      const controller = new AbortController();
      requestControllerRef.current = controller;

      if (silent) {
        if (mountedRef.current) setRefreshing(true);
      } else {
        if (mountedRef.current) setLoading(true);
      }
      if (mountedRef.current) setError("");

      try {
        const response = await api.get("/admin/dashboard/overview", {
          params: { period },
          signal: controller.signal,
        });
        if (mountedRef.current && !controller.signal.aborted) {
          setOverview(response.data || defaultOverview);
        }
      } catch (err) {
        if (
          mountedRef.current &&
          err.name !== "CanceledError" &&
          err.code !== "ERR_CANCELED"
        ) {
          setError(t("admin_overview_error"));
        }
      } finally {
        if (mountedRef.current && !controller.signal.aborted) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [period, t]
  );

  const fetchSensorLocations = useCallback(async () => {
    locationsRequestControllerRef.current?.abort();
    const controller = new AbortController();
    locationsRequestControllerRef.current = controller;

    if (mountedRef.current) {
      setLocationsLoading(true);
      setLocationsError("");
    }

    try {
      const response = await api.get("/admin/dashboard/sensor-locations", {
        signal: controller.signal,
      });

      if (mountedRef.current && !controller.signal.aborted) {
        setSensorLocations(response.data?.sensors || []);
        setLocationsGeneratedAt(response.data?.generatedAt || null);
        setLocationsLoaded(true);
      }
    } catch (err) {
      if (
        mountedRef.current &&
        err.name !== "CanceledError" &&
        err.code !== "ERR_CANCELED"
      ) {
        setLocationsError(t("admin_overview_map_error"));
      }
    } finally {
      if (mountedRef.current && !controller.signal.aborted) {
        setLocationsLoading(false);
      }
    }
  }, [t]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestControllerRef.current?.abort();
      locationsRequestControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    if (viewMode === "map" && !locationsLoaded) {
      fetchSensorLocations();
    }
  }, [fetchSensorLocations, locationsLoaded, viewMode]);

  const usersTrend = useMemo(
    () =>
      (overview.trends?.users || []).map((item) => ({
        ...item,
        count: Number(item.count || 0),
      })),
    [overview.trends?.users]
  );

  const sensorsTrend = useMemo(
    () =>
      (overview.trends?.sensors || []).map((item) => ({
        ...item,
        count: Number(item.count || 0),
      })),
    [overview.trends?.sensors]
  );

  const handleRefresh = () => {
    if (viewMode === "map") {
      fetchSensorLocations();
      return;
    }

    fetchOverview({ silent: true });
  };

  const displayedGeneratedAt =
    viewMode === "map" ? locationsGeneratedAt : overview.generatedAt;
  const refreshDisabled =
    viewMode === "map" ? locationsLoading : loading || refreshing;
  const refreshInProgress =
    viewMode === "map" ? locationsLoading : refreshing;

  return (
    <main className="admin-overview-page">
      <header className="admin-overview-header">
        <div className="admin-overview-page-title">
          <span className="admin-overview-eyebrow">SMARTFARM</span>
          <h1>{t("admin_overview_title")}</h1>
          <p>{t("admin_overview_subtitle")}</p>
        </div>
        <div className="admin-overview-actions">
          <div className="admin-overview-updated" aria-live="polite">
            <span className="admin-overview-live-dot" aria-hidden="true" />
            {t("admin_overview_last_updated")}{" "}
            {formatDateTime(displayedGeneratedAt, language)}
          </div>
          <div className="admin-overview-controls">
            <div
              className="admin-overview-view-switch"
              role="tablist"
              aria-label={t("admin_overview_view_selector")}
            >
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === "overview"}
                className={`admin-overview-view-option ${
                  viewMode === "overview" ? "is-active" : ""
                }`}
                onClick={() => setViewMode("overview")}
              >
                <LayoutDashboard size={16} aria-hidden="true" />
                <span>{t("admin_overview_view_dashboard")}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === "map"}
                className={`admin-overview-view-option ${
                  viewMode === "map" ? "is-active" : ""
                }`}
                onClick={() => setViewMode("map")}
              >
                <MapIcon size={16} aria-hidden="true" />
                <span>{t("admin_overview_view_map")}</span>
              </button>
            </div>
            {viewMode === "overview" && (
              <div className="admin-overview-periods" aria-label={t("period")}>
                {PERIOD_OPTIONS.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    aria-pressed={period === option.value}
                    className={
                      "admin-overview-period " +
                      (period === option.value ? "is-active" : "")
                    }
                    onClick={() => setPeriod(option.value)}
                  >
                    {t(option.labelKey)}
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              className="admin-overview-refresh"
              onClick={handleRefresh}
              disabled={refreshDisabled}
            >
              <Refresh
                className={refreshInProgress ? "is-spinning" : ""}
                fontSize="small"
              />
              <span>
                {refreshInProgress ? t("loading") : t("admin_overview_refresh")}
              </span>
            </button>
          </div>
        </div>
      </header>

      {error && viewMode === "overview" && (
        <div className="admin-overview-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={() => fetchOverview()}>
            {t("retry")}
          </button>
        </div>
      )}

      {viewMode === "map" && (
        <section className="admin-overview-card admin-overview-map-card">
          <div className="admin-overview-card-title admin-overview-map-title">
            <div>
              <span className="admin-overview-card-kicker">SMARTFARM</span>
              <h2>{t("admin_overview_map_title")}</h2>
              <p>{t("admin_overview_map_subtitle")}</p>
            </div>
            <span className="admin-overview-map-count">
              {t("admin_overview_map_count", {
                count: sensorLocations.length,
              })}
            </span>
          </div>
          <AdminSensorsMap
            sensors={sensorLocations}
            loading={locationsLoading}
            error={locationsError}
            onRetry={fetchSensorLocations}
            t={t}
            language={language}
          />
        </section>
      )}

      {viewMode === "overview" && (
        <section
          className="admin-overview-grid"
          aria-label={t("admin_overview_title")}
        >
          {summaryCards.map((card) => (
            <StatCard
              key={card.key}
              card={card}
              value={overview.summary?.[card.key]}
              change={overview.summaryChanges?.[card.key]}
              loading={loading}
              t={t}
              language={language}
            />
          ))}
        </section>
      )}

      {viewMode === "overview" && (
        <section className="admin-overview-chart-grid">
        <article className="admin-overview-card admin-overview-chart-card">
          <div className="admin-overview-card-title">
            <div>
              <span className="admin-overview-card-kicker">
                {t("admin_overview_count")}
              </span>
              <h2>{t("admin_overview_users_trend")}</h2>
            </div>
            <span
              className="admin-overview-chart-indicator is-users"
              aria-hidden="true"
            />
          </div>
          <div className="admin-overview-chart">
            {loading ? (
              <ChartSkeleton />
            ) : (
              <UsersTrendChart
                data={usersTrend}
                language={language}
                emptyLabel={t("admin_overview_no_data")}
              />
            )}
          </div>
        </article>

        <article className="admin-overview-card admin-overview-chart-card">
          <div className="admin-overview-card-title">
            <div>
              <span className="admin-overview-card-kicker">
                {t("admin_overview_count")}
              </span>
              <h2>{t("admin_overview_sensors_trend")}</h2>
            </div>
            <span
              className="admin-overview-chart-indicator is-sensors"
              aria-hidden="true"
            />
          </div>
          <div className="admin-overview-chart">
            {loading ? (
              <ChartSkeleton />
            ) : (
              <SensorsTrendChart
                data={sensorsTrend}
                language={language}
                emptyLabel={t("admin_overview_no_data")}
              />
            )}
          </div>
        </article>

        <article className="admin-overview-card admin-overview-chart-card admin-overview-role-card">
          <div className="admin-overview-card-title">
            <div>
              <span className="admin-overview-card-kicker">SMARTFARM</span>
              <h2>{t("admin_overview_user_distribution")}</h2>
            </div>
          </div>
          <div className="admin-overview-chart admin-overview-role-content">
            {loading ? (
              <ChartSkeleton />
            ) : (
              <RoleDistribution
                roles={overview.userRoles || []}
                t={t}
                language={language}
              />
            )}
          </div>
        </article>
        </section>
      )}

      {viewMode === "overview" && (
        <section className="admin-overview-card admin-overview-recent">
        <div className="admin-overview-card-title admin-overview-recent-title">
          <div>
            <span className="admin-overview-card-kicker">SMARTFARM</span>
            <h2>{t("admin_overview_recent_activity")}</h2>
          </div>
          <div className="admin-overview-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "users"}
              className={`admin-overview-tab ${
                activeTab === "users" ? "is-active" : ""
              }`}
              onClick={() => setActiveTab("users")}
            >
              {t("admin_overview_latest_users")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "installations"}
              className={`admin-overview-tab ${
                activeTab === "installations" ? "is-active" : ""
              }`}
              onClick={() => setActiveTab("installations")}
            >
              {t("admin_overview_latest_installations")}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="admin-overview-list-loading">
            <div className="admin-overview-skeleton line" />
            <div className="admin-overview-skeleton line" />
          </div>
        ) : activeTab === "users" ? (
          <RecentUsers
            users={overview.recent?.users || []}
            t={t}
            language={language}
          />
        ) : (
          <RecentInstallations
            installations={overview.recent?.sensorInstallations || []}
            t={t}
            language={language}
          />
        )}
        </section>
      )}
    </main>
  );
};

export default AdminOverview;
